/**
 * useVigenciaRules — single source of truth for the "≤ N meses" copy
 * shown next to documents that have a freshness window, AND for the
 * per-tenant ``ocrEnabled`` flag the UI uses to skip the OCR dropzone
 * on plans without OCR.
 *
 * Cache strategy
 * ──────────────
 * Cached at module scope so multiple components mounted on the same
 * page only fire ONE request to /vigencia-rules. **However**, the
 * cache is keyed by the current org's ``plan`` value (pulled from
 * ``OrganizationContext``). When a SuperAdmin changes the plan
 * out-of-band, ``OrganizationContext`` picks it up (focus listener)
 * and the next render of this hook sees a different plan key →
 * cache is busted and the fetch repeats. Without this key, the
 * module cache would keep returning ``ocrEnabled: false`` indefinitely
 * even after the org was upgraded to Pro.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useOrganization } from "../../context/OrganizationContext";

const API = process.env.REACT_APP_BACKEND_URL;

// Per-plan cache. The key is the plan string ("free" / "standard" /
// "pro" / "enterprise" / "unknown"). When OrganizationContext refreshes
// and the plan changes, the cache for the OLD plan still exists but
// the hook will request the NEW plan's entry — no stale reads.
const _planCache = new Map();      // planKey → { rules, ocrEnabled }
const _planInflight = new Map();   // planKey → Promise

async function fetchRulesForPlan(planKey) {
  if (_planCache.has(planKey)) return _planCache.get(planKey);
  if (_planInflight.has(planKey)) return _planInflight.get(planKey);
  const p = axios
    .get(`${API}/api/customer-drafts/vigencia-rules`, { withCredentials: true })
    .then((r) => {
      const entry = {
        rules: r.data?.rules || {},
        ocrEnabled: !!r.data?.ocr_enabled,
      };
      _planCache.set(planKey, entry);
      return entry;
    })
    .catch(() => {
      // Conservative fallback: empty rules, OCR DISABLED. Better to
      // show manual capture than to make the user wait for a poll
      // that's never going to resolve.
      const entry = { rules: {}, ocrEnabled: false };
      _planCache.set(planKey, entry);
      return entry;
    })
    .finally(() => { _planInflight.delete(planKey); });
  _planInflight.set(planKey, p);
  return p;
}

export default function useVigenciaRules() {
  // Read plan from OrganizationContext so a SuperAdmin upgrade
  // (picked up by the context's focus listener) invalidates the
  // cache used here. Falls back to "unknown" before the context
  // has hydrated so the first render isn't blocked.
  let plan = "unknown";
  try {
    const ctx = useOrganization();
    plan = ctx?.plan || "unknown";
  } catch {
    // Hook used outside the provider (e.g. on the login screen).
    // The default cache key keeps it working.
  }

  const [data, setData] = useState(
    _planCache.get(plan) || { rules: {}, ocrEnabled: false },
  );

  useEffect(() => {
    let alive = true;
    fetchRulesForPlan(plan).then((r) => { if (alive) setData(r); });
    return () => { alive = false; };
  }, [plan]);

  /** Returns " (≤ N meses)" for a doc_type with a vigencia rule, or "". */
  function suffixFor(docType) {
    const r = data.rules?.[docType];
    if (!r || !r.max_months) return "";
    return ` (≤ ${r.max_months} ${r.max_months === 1 ? "mes" : "meses"})`;
  }

  return { rules: data.rules, ocrEnabled: data.ocrEnabled, suffixFor };
}
